/**
 * Foundation/Anagrafica — standard di validazione P-UX-07.
 * Nessun messaggio durante la digitazione: un errore compare all'uscita dal campo
 * (onBlur) o al salvataggio, e scompare appena il dato diventa corretto.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ITALIAN_PROVINCES_SET } from "@/lib/constants/italianProvinces";
import {
  validateCodiceFiscale, validatePartitaIVA, validateCAP, validateProvincia, validateEmailSyntax,
} from "./validators";

export const ANAGRAFICA_VALIDATORS = {
  tax_code: validateCodiceFiscale,
  vat: validatePartitaIVA,
  cap: validateCAP,
  provincia: (v) => validateProvincia(v, ITALIAN_PROVINCES_SET),
  pec: validateEmailSyntax,
};

export function useAnagraficaValidation(form, validators = ANAGRAFICA_VALIDATORS) {
  const [touched, setTouched] = useState({});
  const errors = useMemo(
    () => Object.fromEntries(Object.entries(validators).map(([k, fn]) => [k, fn(form?.[k])])),
    [form, validators],
  );

  useEffect(() => {
    setTouched((t) => {
      const fixed = Object.keys(t).filter((k) => t[k] && !errors[k]);
      if (!fixed.length) return t;
      const next = { ...t };
      fixed.forEach((k) => { next[k] = false; });
      return next;
    });
  }, [errors]);

  const onBlurField = useCallback((name) => setTouched((t) => ({ ...t, [name]: true })), []);
  const revealAll = useCallback(() => setTouched(Object.fromEntries(Object.keys(validators).map((k) => [k, true]))), [validators]);
  const reset = useCallback(() => setTouched({}), []);
  const visible = useMemo(
    () => Object.fromEntries(Object.keys(errors).map((k) => [k, touched[k] ? errors[k] : null])),
    [errors, touched],
  );
  const firstError = Object.values(errors).find(Boolean) || null;

  return { errors, visible, firstError, hasErrors: !!firstError, onBlurField, revealAll, reset };
}
